import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Alert, Button, FormField, HelpPanel, Select, Spinner } from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { LocaleSource, SupportedLocale } from '@amzn/red-velvet-api';
import { P0_SUPPORTED_LANGUAGES } from '../../data/supportedLanguages';
import { browserLanguages, resolvePickerLocale, toOfferedLocale } from '../../utils/localeUtils';
import { useMessages } from '../../hooks/messages';
import { useGetUserPreference, useUpdateUserPreference } from '../../hooks/userPreferences';
import { publishKatalMetric } from '../katalAnalytics';
import { useUser } from '../../hooks/user';
import { writeCachedPreference } from '../../utils/localePreferenceCache';
import {
  LANGUAGE_PICKER_CONTROL_ID,
  LANGUAGE_PICKER_DROPDOWN,
  LANGUAGE_PICKER_SELECT,
  WEBSITE_COMMON_BUNDLE,
} from '../../utils/constants';

const LANGUAGE_OPTIONS: SelectProps.Option[] = P0_SUPPORTED_LANGUAGES.map(({ locale, label }) => ({
  value: locale,
  label,
}));

// Shown only when the pack these would otherwise come from could not be loaded, so they cannot be
// translated themselves.
const UNTRANSLATED_UNAVAILABLE =
  'Language options are unavailable right now. Please try again soon.';
const UNTRANSLATED_CLOSE = 'Close';

export type LanguagePickerPanelProps = {
  onDismiss: () => void;
};

/**
 * The "Change language" panel opened from the alias menu.
 *
 * Shows the user's language, falling back to their browser's when they have never chosen one, and
 * reloads the page once a new choice is saved so it takes effect everywhere.
 *
 */
export function LanguagePickerPanel({ onDismiss }: LanguagePickerPanelProps) {
  const { message, isLoading: isBundleLoading } = useMessages(WEBSITE_COMMON_BUNDLE);
  const { userId } = useUser();
  const preferenceQuery = useGetUserPreference();
  const updatePreference = useUpdateUserPreference();
  const panelRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const [pendingLocale, setPendingLocale] = useState<SupportedLocale | undefined>();

  useEffect(() => {
    // The menu that opened the panel has closed, so focus needs somewhere to land.
    openerRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
  }, []);

  const dismiss = () => {
    // Only on a deliberate close. The panel also closes on navigation, where taking focus back would
    // pull the user away from the page they just opened.
    if (openerRef.current?.isConnected) {
      openerRef.current.focus();
    }
    onDismiss();
  };

  // What the user is actually set to, as opposed to the fallback shown when that is unknown.
  const knownPersistedLocale = preferenceQuery.isError
    ? undefined
    : toOfferedLocale(preferenceQuery.data?.locale);
  const resolvedLocale = resolvePickerLocale(preferenceQuery.data?.locale, browserLanguages());
  const selectedLocale = pendingLocale ?? resolvedLocale;
  const selectedOption = preferenceQuery.isLoading
    ? null
    : (LANGUAGE_OPTIONS.find((option) => option.value === selectedLocale) ?? null);

  const onLanguageChange: SelectProps['onChange'] = ({ detail }) => {
    const locale = toOfferedLocale(detail.selectedOption.value);
    // Compared against the saved language, not the one on screen, so confirming a fallback still
    // saves it.
    if (!locale || locale === knownPersistedLocale) {
      return;
    }

    setPendingLocale(locale);
    publishKatalMetric(LANGUAGE_PICKER_SELECT, locale);
    updatePreference.mutate(
      { locale },
      {
        onSuccess: () => {
          writeCachedPreference(userId, { locale, source: LocaleSource.EXPLICIT_PREFERENCE });
          window.location.reload();
        },
        onError: () => setPendingLocale(undefined),
      }
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') {
      return;
    }
    // Escape from the open dropdown closes only the dropdown; a second one closes the panel.
    if (!panelRef.current?.contains(event.target as Node)) {
      return;
    }
    dismiss();
  };

  const title = message('languagePicker_title');
  // The panel is the one place a user can change language, so when its own text cannot be loaded it
  // says so in English instead of showing blank labels and an unnamed close button.
  const textIsUnavailable = !isBundleLoading && !title;

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      className="vc-language-panel"
      aria-label={title || undefined}
      aria-busy={isBundleLoading}
      onKeyDown={onKeyDown}
      data-testid="language-picker-panel"
    >
      {isBundleLoading ? (
        <HelpPanel>
          <Spinner data-testid="language-picker-loading" />
        </HelpPanel>
      ) : (
        <>
          <div className="vc-language-panel__dismiss">
            <Button
              variant="icon"
              iconName="close"
              ariaLabel={textIsUnavailable ? UNTRANSLATED_CLOSE : message('languagePicker_close')}
              onClick={dismiss}
              data-testid="language-picker-close"
            />
          </div>
          {textIsUnavailable ? (
            <HelpPanel>
              <Alert type="error" data-testid="language-picker-unavailable">
                {UNTRANSLATED_UNAVAILABLE}
              </Alert>
            </HelpPanel>
          ) : (
            <HelpPanel header={<h2>{title}</h2>}>
              <FormField
                // Fixed id. App.scss uses it to cap the list height.
                controlId={LANGUAGE_PICKER_CONTROL_ID}
                label={message('languagePicker_label')}
                i18nStrings={{ errorIconAriaLabel: message('languagePicker_errorIconLabel') }}
                // Shown in red under the picker, and read out, when the save fails.
                errorText={updatePreference.isError ? message('languagePicker_error') : undefined}
              >
                <Select
                  data-testid="language-picker-select"
                  data-aci-analytics-name={LANGUAGE_PICKER_DROPDOWN}
                  ariaLabel={message('languagePicker_label')}
                  selectedOption={selectedOption}
                  options={LANGUAGE_OPTIONS}
                  onChange={onLanguageChange}
                  filteringType="auto"
                  filteringPlaceholder={message('languagePicker_filteringPlaceholder')}
                  placeholder={message('languagePicker_loading')}
                  disabled={preferenceQuery.isLoading || updatePreference.isPending}
                  // Keeps the list clear of the panel's edges, and keeps Escape closing one layer at a time.
                  expandToViewport={true}
                />
              </FormField>
              {preferenceQuery.isError && (
                <Alert
                  type="warning"
                  statusIconAriaLabel={message('languagePicker_warningIconLabel')}
                  data-testid="language-picker-read-error"
                >
                  {message('languagePicker_readError')}
                </Alert>
              )}
            </HelpPanel>
          )}
        </>
      )}
    </aside>
  );
}
